import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Info,
  PackageMinus,
  Phone,
  Receipt,
  ShieldAlert,
  ShoppingCart,
} from 'lucide-react';
import type { Notification, NotificationCategory, NotificationSeverity } from '@erp/shared';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/formatters';

const CATEGORY_ICONS: Record<NotificationCategory, typeof Info> = {
  STOCK_LOW: PackageMinus,
  BATCH_EXPIRING: CalendarClock,
  ORDER_CREATED: ShoppingCart,
  INVOICE_OVERDUE: Receipt,
  TASK_ASSIGNED: Phone,
  SYSTEM: ShieldAlert,
  PRODUCT_SPECIFICATION: ClipboardCheck,
};

const SEVERITY_STYLES: Record<NotificationSeverity, string> = {
  INFO: 'text-muted-foreground',
  SUCCESS: 'text-emerald-600 dark:text-emerald-400',
  WARNING: 'text-amber-600 dark:text-amber-400',
  CRITICAL: 'text-destructive',
};

const SEVERITY_ICONS: Record<NotificationSeverity, typeof Info> = {
  INFO: Info,
  SUCCESS: CheckCircle2,
  WARNING: AlertTriangle,
  CRITICAL: AlertTriangle,
};

export interface NotificationItemProps {
  notification: Notification;
  onSelect: (notification: Notification) => void;
}

export function NotificationItem({ notification, onSelect }: NotificationItemProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const CategoryIcon = CATEGORY_ICONS[notification.category];
  const SeverityIcon = SEVERITY_ICONS[notification.severity];

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(notification)}
        className={cn(
          'flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          notification.isRead ? 'opacity-70' : 'bg-accent/40',
        )}
      >
        <span className={cn('mt-0.5 shrink-0', SEVERITY_STYLES[notification.severity])}>
          <CategoryIcon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{notification.title}</span>
            {notification.isRead ? null : (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                aria-label={t('notifications.unread')}
              />
            )}
          </span>
          <span className="line-clamp-2 text-xs text-muted-foreground">{notification.message}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <SeverityIcon
              className={cn('h-3 w-3', SEVERITY_STYLES[notification.severity])}
              aria-hidden="true"
            />
            {t(`notifications.categories.${notification.category}`)} ·{' '}
            {formatDateTime(notification.createdAt, i18n.language)}
          </span>
        </span>
      </button>
    </li>
  );
}
